// ---------------------------------------------------------------------------
// ENRUTADOR POR SUBDOMINIO con CODE SPLITTING.
//
// Según el host se baja UNA sola aplicación:
//   - admin.soedia.com / admin.localhost  → backoffice del SUPER ADMIN
//   - TODO lo demás (incluido localhost sin subdominio) → la app del negocio
//
// React.lazy emite cada app en su propio chunk: el usuario normal NUNCA
// descarga el bundle del backoffice (y viceversa). La lógica del subdominio
// vive en lib/tenant.js (parseApp), aislada de este componente.
// ---------------------------------------------------------------------------
import { Suspense, lazy } from 'react';
import { Loader2 } from 'lucide-react';
import { parseApp } from './lib/tenant.js';
import { t } from './lib/i18n.js';

const SuperAdminApp = lazy(() => import('./superadmin/SuperAdminApp.jsx'));
const TenantApp = lazy(() => import('./TenantApp.jsx'));

// Los enlaces de cuenta (crear/restablecer contraseña, confirmar correo) tienen su propia
// página y no dependen de la app: sirven igual en admin.soedia.com que en un negocio.
const AccountActionPage = lazy(() => import('./components/AccountActionPage.jsx'));
const isAccountAction = typeof window !== 'undefined' && ['/auth/action', '/auth/invite'].includes(window.location.pathname);

// Registro público, en cualquier host: /crear (negocio con prueba gratis) y /personal (cuenta
// personal gratis para siempre, solo Finanzas).
const SignupPage = lazy(() => import('./components/SignupPage.jsx'));
const signupPath = typeof window !== 'undefined' ? window.location.pathname.replace(/\/+$/, '') : '';
const isSignup = signupPath === '/crear' || signupPath === '/personal';
const PersonalSignupPage = () => <SignupPage personal />;

const AppRoot = isAccountAction ? AccountActionPage : isSignup ? (signupPath === '/personal' ? PersonalSignupPage : SignupPage) : parseApp() === 'admin' ? SuperAdminApp : TenantApp;

/** Mientras se baja el chunk de la app (solo la primera vez y con red lenta). */
function AppFallback() {
  return (
    <div className="flex min-h-dvh items-center justify-center text-stone-500" role="status" aria-label={t('Cargando aplicación')}>
      <Loader2 size={26} className="animate-spin" />
    </div>
  );
}

/** SOLO `npm run dev`: salto entre el backoffice (?as=admin) y la app del negocio. */
function DevSwitch() {
  const admin = parseApp() === 'admin';
  return (
    <a
      href={admin ? window.location.pathname : '/?as=admin'}
      className="fixed bottom-20 left-3 z-50 rounded bg-amber-300 px-2 py-1 text-xs font-semibold text-gray-950 shadow lg:bottom-3"
    >
      DEV · {admin ? 'Ir al negocio' : 'Ir a Super Admin'}
    </a>
  );
}

export default function App() {
  const Root = AppRoot;
  return (
    <Suspense fallback={<AppFallback />}>
      <Root />
      {import.meta.env.DEV && <DevSwitch />}
    </Suspense>
  );
}
